// Lead Generation System — Lead Generator Home (earnings-first console
// dashboard). Phoenix Capital is the Loan Administrator; the generator's job
// is to feed the production line, so this surface leads with THIS WEEK'S
// earnings, then the funnel, the conversion ratio, and a coaching signal.
//
// Portal rules: no React Query — the engine's useWidgetData is a plain
// useEffect + useState fetch with a cancel guard. Only /api/portal/* calls.

import { useMemo } from "react";
import { Button } from "@/design-system/components/ui";
import { formatCurrency, formatNumber } from "@/design-system/lib/format";
import {
  DashboardGrid,
  NextBestActionCard,
  ProgressBar,
  StatTile,
  StatTileRow,
} from "../../dashboard/DashboardPrimitives";
import { PortalHomeLayout, useWidgetData, WidgetFrame } from "../../dashboard/engine";
import {
  leadGeneratorApi,
  type LeadGenEarningsSummaryDto,
  type PreleadStatsDto,
} from "../../api/leadGenerator";
import { leadGenGradeApi, type LeadGenGradeResult } from "../../api/leadGenGrade";
import { LeadGenGradeChip } from "./GradeBadge";
import { buildFunnel, interestedPer100 } from "./pipeline";

/** Cents everywhere on this surface (Part A #1) — 2-dp, never whole dollars. */
function money(amount: number | null | undefined): string {
  return formatCurrency(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function LeadGenHomeTab({ onGoToPreleads }: { onGoToPreleads: () => void }) {
  // Primary fetch — this week's earnings gate the page (cheap, always present).
  const earnings = useWidgetData<LeadGenEarningsSummaryDto>(
    async () => leadGeneratorApi.earnings(),
    [],
  );
  // Secondary — the funnel/ratio degrade independently in their own card.
  const stats = useWidgetData<PreleadStatsDto>(async () => leadGeneratorApi.getStats(), []);
  // The authoritative grade (same endpoint as the Earnings badge) — surfaced as
  // a compact chip in the hero. Never an FE-computed grade.
  const grade = useWidgetData<LeadGenGradeResult>(async () => leadGenGradeApi.grade(), []);
  const summary = earnings.data;

  return (
    <PortalHomeLayout
      loading={summary == null && earnings.status === "loading"}
      error={summary == null ? earnings.error : null}
      onRetry={earnings.reload}
      testId="leadgen-home"
    >
      {summary != null && (
        <>
          <EarningsHero
            summary={summary}
            grade={grade.data}
            onGoToPreleads={onGoToPreleads}
          />
          <DashboardGrid>
            <FunnelWidget state={stats} />
            <CoachingWidget state={stats} />
          </DashboardGrid>
        </>
      )}
    </PortalHomeLayout>
  );
}

// ── Earnings hero ────────────────────────────────────────────────────────────

function EarningsHero({
  summary,
  grade,
  onGoToPreleads,
}: {
  summary: LeadGenEarningsSummaryDto;
  grade: LeadGenGradeResult | null;
  onGoToPreleads: () => void;
}) {
  return (
    <WidgetFrame
      title="This week"
      span="full"
      testId="leadgen-home-earnings"
      action={
        <div className="flex items-center gap-space-2">
          {grade && <LeadGenGradeChip grade={grade} />}
          <Button
            variant="primary"
            size="sm"
            onClick={onGoToPreleads}
            data-testid="leadgen-home-go-line"
          >
            Work the line
          </Button>
        </div>
      }
    >
      <div className="space-y-space-4">
        <StatTileRow>
          <StatTile
            label="Credited this week"
            value={money(summary.totalCredited)}
            hint="What's counted so far"
            tone="success"
            testId="leadgen-home-credited"
          />
          <StatTile
            label="Earned"
            value={money(summary.totalEarned)}
            hint="Before caps"
            testId="leadgen-home-earned"
          />
          <StatTile
            label="Capped"
            value={money(summary.totalCapped)}
            hint="Held back by weekly / monthly caps"
            tone={summary.totalCapped > 0 ? "warning" : "default"}
            testId="leadgen-home-capped"
          />
          <StatTile
            label="Actions credited"
            value={formatNumber(summary.totalCount)}
            hint="Credited KPI actions this week"
            testId="leadgen-home-count"
          />
        </StatTileRow>

        {summary.weeklyTotalCap != null && (
          <ProgressBar
            fraction={summary.weeklyTotalCap > 0 ? summary.totalCredited / summary.weeklyTotalCap : 0}
            label={`${money(summary.totalCredited)} of your ${money(
              summary.weeklyTotalCap,
            )} weekly cap`}
            testId="leadgen-home-cap-progress"
          />
        )}

        {summary.buckets.length > 0 && (
          <div className="space-y-space-2" data-testid="leadgen-home-buckets">
            <p className="text-caption uppercase tracking-wide text-gray-mid m-0">By bucket</p>
            <ul className="flex flex-col gap-space-1 list-none p-0 m-0">
              {summary.buckets.map((b) => (
                <li
                  key={b.bucket}
                  className="flex items-center justify-between gap-space-3 text-small"
                >
                  <span className="text-onyx truncate">{b.bucket}</span>
                  <span className="shrink-0 text-gray-mid">
                    <span className="text-status-success font-semibold">
                      {money(b.credited)}
                    </span>{" "}
                    credited
                    {b.capped > 0 && ` · ${money(b.capped)} capped`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-caption text-gray-mid m-0">
          These figures are a display-only estimate of what you've racked up. Your manager reviews
          and finalizes what's payable, and pay is handled off-system.
        </p>
      </div>
    </WidgetFrame>
  );
}

// ── Funnel ───────────────────────────────────────────────────────────────────

function FunnelWidget({
  state,
}: {
  state: ReturnType<typeof useWidgetData<PreleadStatsDto>>;
}) {
  const stats = state.data;
  const funnel = useMemo(() => (stats ? buildFunnel(stats) : []), [stats]);
  const top = funnel[0]?.count ?? 0;

  return (
    <WidgetFrame title="Your funnel" span="full" state={state} testId="leadgen-home-funnel">
      {stats == null ? null : stats.total === 0 ? (
        <p className="text-small text-gray-mid m-0">
          Paste your first listing to start building your funnel.
        </p>
      ) : (
        <div className="space-y-space-3">
          {funnel.map((stage) => (
            <div key={stage.key} className="space-y-space-1">
              <div className="flex items-center justify-between gap-space-2">
                <span className="text-small text-onyx">{stage.label}</span>
                <span className="text-small font-semibold text-onyx">
                  {formatNumber(stage.count)}
                </span>
              </div>
              <ProgressBar fraction={top > 0 ? stage.count / top : 0} />
            </div>
          ))}
        </div>
      )}
    </WidgetFrame>
  );
}

// ── Coaching / conversion ratio ──────────────────────────────────────────────

function CoachingWidget({
  state,
}: {
  state: ReturnType<typeof useWidgetData<PreleadStatsDto>>;
}) {
  const stats = state.data;
  const per100 = stats ? interestedPer100(stats) : null;
  // Enough volume for the conversion signal to mean something.
  const hasVolume = stats != null && stats.total >= 5;

  return (
    <WidgetFrame title="Coaching" span="full" state={state} testId="leadgen-home-coaching">
      {stats == null ? null : (
        <div className="space-y-space-4">
          <StatTileRow>
            <StatTile
              label="Interested / 100"
              value={per100 != null ? formatNumber(per100) : "—"}
              hint="Preleads that reach interested, per 100 created"
              testId="leadgen-home-ratio"
            />
            <StatTile
              label="Total created"
              value={formatNumber(stats.total)}
              testId="leadgen-home-total"
            />
          </StatTileRow>

          <NextBestActionCard
            heading={hasVolume ? "Keep the line moving" : "Get some preleads in the pipe"}
            description={
              hasVolume
                ? "Focus on better-fit listings to lift your interested rate. Your official grade and bonus live on the Earnings tab."
                : "Paste a few listings so your conversion and coaching signals can calibrate."
            }
            testId="leadgen-home-next"
          />
        </div>
      )}
    </WidgetFrame>
  );
}
